import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaSearch, FaPlus, FaEdit, FaTrash, FaFileExcel } from "react-icons/fa";
import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";
import TaxCollectorModal from "./TaxCollectorModal";

function TaxCollectorList() {
  const [taxData, setTaxData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchTaxData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tax-dapartment-list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setTaxData(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch tax data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxData();
  }, []);

  const handleAdd = () => {
    setEditingData(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setShowModal(true);
  };

  const handleDelete = async (tax_id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const response = await axios.post(
          `${API_URL}/tax-department-delete.php`,
          { tax_id },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.success) {
          toast.success("Tax record deleted successfully!");
          fetchTaxData();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("An error occurred during deletion.");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingData(null);
  };

  const handleSave = () => {
    fetchTaxData();
    handleModalClose();
  };

  const filteredData = useMemo(() => {
    return taxData.filter((item) => {
      const matchesPaymentType =
        paymentTypeFilter === "all" || item.tax_type === paymentTypeFilter;
      const itemDate = new Date(item.tax_date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;

      return matchesPaymentType;
    });
  }, [taxData, startDate, endDate, paymentTypeFilter]);

  const totalAmount = useMemo(
    () =>
      filteredData.reduce(
        (sum, item) => sum + parseFloat(item.tax_amount || 0),
        0
      ),
    [filteredData]
  );

  const handleExport = () => {
    const dataToExport = filteredData.map((d) => ({
      Date: d.tax_date,
      Amount: d.tax_amount,
      "Payment Type": d.tax_type,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TaxData");
    XLSX.writeFile(workbook, "Tax_Collection_Report.xlsx");
  };

  const columns = [
    {
      name: "Collection Date",
      selector: (row) => row.tax_date,
      sortable: true,
    },
    {
      name: "Amount (₹)",
      selector: (row) => parseFloat(row.tax_amount).toLocaleString("en-IN"),
      sortable: true,
    },
    {
      name: "Payment Type",
      selector: (row) => row.tax_type,
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.tax_type}</span>,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <FaEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row.tax_id)}
          >
            <FaTrash />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
            >
              Tax Collection Management
              <Button variant="primary" size="sm" onClick={handleAdd}>
                <FaPlus /> Add New Entry
              </Button>
            </Card.Header>
            <Card.Body>
              <Form className="p-3 border-bottom">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label>Payment Type</Form.Label>
                    <Form.Select
                      value={paymentTypeFilter}
                      onChange={(e) => setPaymentTypeFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3} className="text-md-end">
                    <Button onClick={handleExport} variant="success">
                      <FaFileExcel className="me-2" />
                      Export
                    </Button>
                  </Col>
                </Row>
              </Form>
              <Row className="p-3 text-center">
                <Col>
                  <Card bg="light">
                    <Card.Body>
                      <Card.Title>Total Tax Collected</Card.Title>
                      <Card.Text className="fs-4 fw-bold">
                        ₹{totalAmount.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                responsive
                progressPending={loading}
              />
            </Card.Body>
          </Card>
        </div>
      </div>
      {showModal && (
        <TaxCollectorModal
          show={showModal}
          handleClose={handleModalClose}
          onSave={handleSave}
          data={editingData}
        />
      )}
    </>
  );
}

export default TaxCollectorList;
